import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './CreateAcquirer.css';
import Loader from '../../../components/shared/Loader';
import CreateFlowHero from '../../../components/shared/CreateFlowHero';
import SearchableSelect from '../../../components/shared/SearchableSelect';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import {
    BsBuilding, BsCheckCircleFill,
    BsArrowRight, BsArrowLeft, BsCheckLg, BsJournalText, BsGear,
    BsEyeFill, BsEyeSlashFill, BsShieldFill, BsLink45Deg, BsGlobe2,
} from 'react-icons/bs';
import { apiService } from '../../../services/apiService';
import { API_PATHS } from '../../../utils/apiPaths';
import {
    validateEmail,
} from '../../../utils/validation';
import type { CountryOption } from '../models/types';

const STEPS = [
    { id: 1, label: 'Configuration', icon: <BsBuilding /> },
    { id: 2, label: 'Access & Documentation', icon: <BsJournalText /> },
    { id: 3, label: 'Review & Confirm', icon: <BsCheckLg /> },
];

const BLANK = {
    name: '',
    country: '',
    countryId: '',
    code: '',
    status: 'ACTIVE',
    supportedCurrencies: [],
    supportedPaymentMethods: [] as Array<{ autoId: number; paymentMethod: string }>,
    settlementDays: 0,
    trafficType: 'API Pool',
    contactEmail: '',
    contactPhone: '',
    notes: '',
    docUrl: '',
    loginUrl: '',
    loginEmail: '',
    loginPassword: '',
};

const isValidUrl = (value?: string) => {
    const trimmed = value?.trim();
    if (!trimmed) return false;
    try {
        const url = new URL(trimmed);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
};

function formatAcquirerCodeInput(value: string) {
    return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

function validateAcquirerCode(value: string) {
    const code = value.trim();
    if (!code) return 'Required';
    if (!/^[A-Za-z0-9]+$/.test(code)) return 'Code must be alphanumeric (letters and numbers only)';
    return '';
}

const renderReviewUrl = (label: string, value?: string) => {
    const trimmed = value?.trim();
    return (
        <div key={label} className="cm-review-row">
            <span>{label}</span>
            {trimmed ? (
                <a href={trimmed} target="_blank" rel="noopener noreferrer">{trimmed}</a>
            ) : (
                <strong>G��</strong>
            )}
        </div>
    );
};

const CreateAcquirer: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [step, setStep] = useState(1);
    const [form, setForm] = useState<any>({ ...BLANK });
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(false);
    const [showPwd, setShowPwd] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [paymentMethodOptions, setPaymentMethodOptions] = useState<Array<{ autoId: number; paymentMethod: string }>>([]);
    const [countries, setCountries] = useState<CountryOption[]>([]);

    const countrySelectOptions = useMemo(
        () =>
            [...countries]
                .sort((a, b) => a.countryName.localeCompare(b.countryName))
                .map((c) => ({
                    value: String(c.autoId),
                    label: c.countryName,
                    searchText: c.countryFlag,
                })),
        [countries],
    );

    useEffect(() => {
        fetchDropdownOptions();
        if (id) {
            fetchAcquirer(id);
        }
    }, [id]);

    useEffect(() => {
        if (!form.country || countries.length === 0 || form.countryId) return;
        const match = countries.find(
            (c) => c.countryName.toLowerCase() === form.country.trim().toLowerCase(),
        );
        if (match) {
            setForm((prev: typeof BLANK) => ({ ...prev, countryId: String(match.autoId) }));
        }
    }, [countries, form.country, form.countryId]);



    const fetchDropdownOptions = async () => {
        try {
            const [methodsRes, countriesRes]: any[] = await Promise.all([
                apiService.get('/getActivePaymentMethods'),
                apiService.get(API_PATHS.GET_ACTIVE_COUNTRIES),
            ]);

            const methodsRaw = Array.isArray(methodsRes?.data)
                ? methodsRes.data
                : Array.isArray(methodsRes)
                    ? methodsRes
                    : [];

            const methods = methodsRaw
                .map((m: any) => {
                    const autoId = Number(m.autoId ?? m.id ?? 0);
                    const paymentMethod = String(
                        m.paymentMethod ?? m.name ?? m.type ?? m.method ?? m.label ?? ''
                    ).trim();
                    return { autoId, paymentMethod };
                })
                .filter((m: { autoId: number; paymentMethod: string }) => m.autoId > 0 && m.paymentMethod.length > 0);

            setPaymentMethodOptions(methods);

            const countriesRaw = Array.isArray(countriesRes?.data)
                ? countriesRes.data
                : Array.isArray(countriesRes)
                    ? countriesRes
                    : [];
            const countryList = countriesRaw
                .map((c: any) => ({
                    autoId: Number(c.autoId ?? c.id ?? 0),
                    countryName: String(c.countryName ?? c.name ?? '').trim(),
                    countryFlag: String(c.countryFlag ?? '').trim().toUpperCase(),
                }))
                .filter(
                    (c: CountryOption) =>
                        c.autoId > 0 && c.countryName.length > 0 && c.countryFlag.length === 2,
                );
            setCountries(countryList);
        } catch (err) {
            console.error('Failed to load dropdown options:', err);
        }
    };

    const fetchAcquirer = async (acqId: string) => {
        setFetchingData(true);
        setApiError(null);
        try {
            const res: any = await apiService.get(`/acquirers/${acqId}`);
            if (res.success) {
                const data = res.data;

                setForm({
                    ...BLANK,
                    name: data.name ?? '',
                    country: data.country ?? '',
                    code: formatAcquirerCodeInput(String(data.code ?? '')),
                    status: data.status ?? 'ACTIVE',
                    settlementDays: data.settlementDays ?? 0,
                    contactEmail: data.email ?? data.email ?? '',
                    contactPhone: data.contactPhone ?? data.contactNumber ?? '',
                    loginUrl: data.loginUrl ?? '',
                    loginEmail: data.loginEmail ?? data.loginEmail ?? '',
                    loginPassword: data.loginPassword ?? '',
                    docUrl: data.documentationUrl ?? data.documentationUrl ?? '',
                    notes: data.notes ?? '',
                    trafficType: data.trafficType ?? data.flows?.[0]?.flowType ?? BLANK.trafficType,
                    supportedCurrencies: data.supportedCurrencies || [],
                    supportedPaymentMethods: (data.supportedPaymentMethods || []).map((m: any) => {
                        if (typeof m === 'string') {
                            return { autoId: 0, paymentMethod: m };
                        }
                        return {
                            autoId: Number(m.autoId ?? m.id ?? 0),
                            paymentMethod: String(m.paymentMethod ?? m.name ?? ''),
                        };
                    }),
                    extraFields: data.extraFields || {},
                });
            } else {
                setApiError('Acquirer not found');
            }
        } catch (err: any) {
            console.error('Fetch Error:', err);
            setApiError('Failed to load acquirer data');
        } finally {
            setFetchingData(false);
        }
    };

    const set = (field: string, value: any) =>
        setForm((prev: any) => ({ ...prev, [field]: value }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target as any;
        if (type === 'number') {
            set(name, parseInt(value) || 0);
        } else if (name === 'code') {
            set(name, formatAcquirerCodeInput(value));
        } else {
            set(name, value);
        }
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: undefined } as any));
        }
    };

    // const handleToggleMulti = (field: string, value: string) => {
    //     const current = [...(form[field] || [])];
    //     const idx = current.indexOf(value);
    //     if (idx > -1) current.splice(idx, 1);
    //     else current.push(value);
    //     set(field, current);
    // };

    const handleTogglePaymentMethod = (method: { autoId: number; paymentMethod: string }) => {
        const current = [...(form.supportedPaymentMethods || [])];
        const idx = current.findIndex((m: { autoId: number; paymentMethod: string }) => m.autoId === method.autoId);
        if (idx > -1) current.splice(idx, 1);
        else current.push(method);
        set('supportedPaymentMethods', current);
        if (errors.supportedPaymentMethods) {
            setErrors((prev) => ({ ...prev, supportedPaymentMethods: undefined } as any));
        }
    };

    const getStepErrors = (s: number): Record<string, string> => {
        const e: Record<string, string> = {};
        if (s === 1 || s === 3) {
            if (!form.name?.trim()) e.name = 'Required';
            if (!form.countryId) e.country = 'Required';
            const codeError = validateAcquirerCode(form.code ?? '');
            if (codeError) e.code = codeError;
            const contactEmailError = validateEmail(form.contactEmail ?? '', {
                required: true,
                requiredMessage: 'Required',
            });
            if (contactEmailError) e.contactEmail = contactEmailError;
            if ((form.supportedPaymentMethods || []).length === 0) e.supportedPaymentMethods = 'Select at least one payment method';
        }
        if (s === 2 || s === 3) {
            if (!form.loginUrl?.trim()) e.loginUrl = 'Required';
            else if (!isValidUrl(form.loginUrl)) e.loginUrl = 'Enter a valid URL (https://...)';
            const loginEmailError = validateEmail(form.loginEmail ?? '', {
                required: true,
                requiredMessage: 'Required',
            });
            if (loginEmailError) e.loginEmail = loginEmailError;
            const docUrl = form.docUrl?.trim();
            if (docUrl && !isValidUrl(docUrl)) e.docUrl = 'Enter a valid URL (https://...)';
        }
        return e;
    };

    const validate = (s: number): boolean => {
        const e = getStepErrors(s);
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const getFirstInvalidStep = (): number | null => {
        if (Object.keys(getStepErrors(1)).length > 0) return 1;
        if (Object.keys(getStepErrors(2)).length > 0) return 2;
        return null;
    };

    const next = () => { if (validate(step)) setStep(s => s + 1); };
    const back = () => { setErrors({}); setStep(s => s - 1); };

    const buildPayload = () => ({
        id: isEdit ? Number(id) : undefined,
        name: form.name,
        country: form.country,
        code: form.code?.trim() || '',
        status: form.status,
        supportedCurrencies: form.supportedCurrencies || [],
        supportedPaymentMethods: form.supportedPaymentMethods || [],
        settlementDays: form.settlementDays || 0,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone?.trim() || undefined,
        notes: form.notes,
        docUrl: form.docUrl,
        loginUrl: form.loginUrl,
        loginEmail: form.loginEmail,
        loginPassword: form.loginPassword,
        trafficType: form.trafficType,
        extraFields: form.extraFields || {},
    });

    const submitAcquirer = async () => {
        const invalidStep = getFirstInvalidStep();
        if (invalidStep) {
            setStep(invalidStep);
            setErrors(getStepErrors(3));
            return;
        }

        setApiError(null);
        setErrors({});
        setLoading(true);
        try {
            const payload = buildPayload();

            if (isEdit) {
                await apiService.post('/updateAcquirer', payload);
            } else {
                await apiService.post('/addAcquirer', payload);
            }
            setDone(true);
        } catch (err: any) {
            console.error('API Error:', err);
            setApiError(err.response?.data?.message || 'Failed to save acquirer. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (step < 3) {
            next();
        }
    };

    const handleReset = () => {
        setForm({ ...BLANK });
        setStep(1);
        setDone(false);
        setErrors({});
        if (isEdit) navigate('/dashboard/acquirers/create');
    };

    if (done) {
        return (
            <div className="cm-page">
                <div className="cm-success-wrap">
                    <div className="cm-success-icon"><BsCheckCircleFill /></div>
                    <h2 className="cm-success-title">Acquirer {isEdit ? 'Updated' : 'Created'} Successfully!</h2>
                    <p className="cm-success-sub">
                        The acquirer <strong>{form.name}</strong> has been {isEdit ? 'updated' : 'registered'} in the system.
                    </p>
                    <div className="cm-success-details">
                        <div className="cm-sdet"><span>Country</span><strong>{form.country}</strong></div>
                        <div className="cm-sdet"><span>Code</span><strong>{form.code}</strong></div>
                        <div className="cm-sdet"><span>Status</span><strong>{form.status}</strong></div>
                        {/* <div className="cm-sdet"><span>Industry</span><strong>{form.industryType}</strong></div> */}
                    </div>
                    <div className="flex gap-4">
                        <Button type="button" variant="outline" onClick={() => navigate('/dashboard/acquirers')}>Back to Acquirers</Button>
                        {!isEdit && <Button type="button" onClick={handleReset}>Create Another</Button>}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cm-page dh-page">
            <CreateFlowHero
                badge={isEdit ? 'Edit Acquirer' : 'New Acquirer'}
                title={isEdit ? 'Edit Acquirer' : 'Create New Acquirer'}
                subtitle={
                    isEdit
                        ? `Modifying configuration for ${form.name}`
                        : 'Step-by-step onboarding for a new payment acquirer'
                }
                icon={<BsBuilding />}
                onBack={() => navigate('/dashboard/acquirers')}
                backLabel="Back to Acquirers"
            />

            {/* Stepper */}
            <div className="cm-stepper">
                {STEPS.map((s, i) => (
                    <React.Fragment key={s.id}>
                        <div className={`cm-step ${step === s.id ? 'active' : step > s.id ? 'done' : ''}`}>
                            <div className="cm-step-circle">
                                {step > s.id ? <BsCheckLg /> : s.icon}
                            </div>
                            <span className="cm-step-label">{s.label}</span>
                        </div>
                        {i < STEPS.length - 1 && <div className={`cm-step-line ${step > s.id ? 'done' : ''}`} />}
                    </React.Fragment>
                ))}
            </div>

            {/* Form Card */}
            <div className="cm-card" style={{ position: 'relative' }}>
                {fetchingData && (
                    <div className="cm-loading-overlay">
                        <Loader size="lg" />
                    </div>
                )}
                <form onSubmit={handleSubmit}>

                    {/* G��G�� Step 1: Configuration G��G�� */}
                    {step === 1 && (
                        <div className="cm-step-body">
                            <h3 className="cm-step-title"><BsGear /> Basic Configuration</h3>
                            <div className="cm-grid">
                                <label className="cm-label cm-span2">
                                    <span>Acquirer Name <em>*</em></span>
                                    <Input
                                        className={cn(errors.name && 'border-destructive')}
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder="e.g. EuroPay GmbH"
                                    />
                                    {errors.name && <p className="cm-err">{errors.name}</p>}
                                </label>
                                <label className="cm-label">
                                    <span>Country <em>*</em></span>
                                    <div className="relative">
                                        <BsGlobe2 className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                                        <SearchableSelect
                                            className="h-9 pl-9"
                                            value={form.countryId}
                                            options={countrySelectOptions}
                                            onChange={(countryId) => {
                                                const country = countries.find((c) => String(c.autoId) === countryId);
                                                set('countryId', countryId);
                                                set('country', country?.countryName ?? '');
                                            }}
                                            placeholder="Select country"
                                            searchPlaceholder="Search countryGǪ"
                                            emptyMessage="No countries available."
                                            error={!!errors.country}
                                            aria-label="Country"
                                        />
                                    </div>
                                    {errors.country && <p className="cm-err">{errors.country}</p>}
                                </label>
                                <label className="cm-label">
                                    <span>Code <em>*</em></span>
                                    <Input
                                        className={cn(errors.code && 'border-destructive')}
                                        name="code"
                                        value={form.code}
                                        onChange={handleChange}
                                        placeholder="e.g. EPC001"
                                        autoComplete="off"
                                    />
                                    {errors.code && <p className="cm-err">{errors.code}</p>}
                                </label>
                                {/* <label className="cm-label">
                                    <span>Status</span>
                                    <select className="cm-input" name="status" value={form.status} onChange={handleChange}>
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="INACTIVE">INACTIVE</option>
                                    </select>
                                </label> */}
                                {/* <label className="cm-label">
                                    <span>Settlement Days</span>
                                    <input type="number" className="cm-input" name="settlementDays" value={form.settlementDays} onChange={handleChange} min="0" />
                                </label> */}
                                {/* <label className="cm-label">
                                    <span>Industry Type</span>
                                    <select className="cm-input" name="industryType" value={form.industryType} onChange={handleChange}>
                                        {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                                    </select>
                                </label> */}
                                {/* <label className="cm-label">
                                    <span>Traffic Type</span>
                                    <select className="cm-input" name="trafficType" value={form.trafficType} onChange={handleChange}>
                                        {TRAFFIC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </label> */}
                                <label className="cm-label">
                                    <span>Contact Email <em>*</em></span>
                                    <Input
                                        type="email"
                                        className={cn(errors.contactEmail && 'border-destructive')}
                                        name="contactEmail"
                                        value={form.contactEmail}
                                        onChange={handleChange}
                                        placeholder="support@acquirer.com"
                                    />
                                    {errors.contactEmail && <p className="cm-err">{errors.contactEmail}</p>}
                                </label>
                                <label className="cm-label">
                                    <span>Contact Phone</span>
                                    <Input
                                        type="tel"
                                        className={cn(errors.contactPhone && 'border-destructive')}
                                        name="contactPhone"
                                        value={form.contactPhone}
                                        onChange={handleChange}
                                        placeholder="8012345678"
                                    />
                                    {errors.contactPhone && <p className="cm-err">{errors.contactPhone}</p>}
                                </label>

                                {/* <div className="cm-span2">
                                    <span className="cm-label-text">Supported Currencies</span>
                                    <div className="cm-multi-select">
                                        {CURRENCIES.map(c => (
                                            <div key={c} className={`cm-multi-item ${form.supportedCurrencies.includes(c) ? 'active' : ''}`} onClick={() => handleToggleMulti('supportedCurrencies', c)}>
                                                {c}
                                            </div>
                                        ))}
                                    </div>
                                    {errors.supportedCurrencies && <p className="cm-err">{errors.supportedCurrencies}</p>}
                                </div> */}

                                <div className="cm-span2">
                                    <span className="cm-label">
                                        <span>Payment Type <em>*</em></span>
                                    </span>
                                    <div className="cm-multi-select">
                                        {paymentMethodOptions.map(p => (
                                            <div
                                                key={p.autoId}
                                                className={`cm-multi-item ${(form.supportedPaymentMethods || []).some((spm: { autoId: number }) => spm.autoId === p.autoId) ? 'active' : ''}`}
                                                onClick={() => handleTogglePaymentMethod(p)}
                                            >
                                                {p.paymentMethod}
                                            </div>
                                        ))}
                                    </div>
                                    {errors.supportedPaymentMethods && <p className="cm-err">{errors.supportedPaymentMethods}</p>}
                                </div>

                            </div>
                        </div>
                    )}

                    {/* G��G�� Step 2: Access & Documentation G��G�� */}
                    {step === 2 && (
                        <div className="cm-step-body">
                            <h3 className="cm-step-title"><BsJournalText /> Access & Documentation</h3>
                            <div className="cm-grid">
                              
                                <label className="cm-label cm-span2">
                                    <span>Documentation URL</span>
                                    <div className="relative">
                                        <BsLink45Deg className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            type="url"
                                            className={cn('pl-9', errors.docUrl && 'border-destructive')}
                                            name="docUrl"
                                            value={form.docUrl}
                                            onChange={handleChange}
                                            placeholder="https://docs.acquirer.com"
                                        />
                                    </div>
                                    {errors.docUrl && <p className="cm-err">{errors.docUrl}</p>}
                                </label>
                                <label className="cm-label cm-span2">
                                    <span>Login URL <em>*</em></span>
                                    <div className="relative">
                                        <BsLink45Deg className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            type="url"
                                            className={cn('pl-9', errors.loginUrl && 'border-destructive')}
                                            name="loginUrl"
                                            value={form.loginUrl}
                                            onChange={handleChange}
                                            placeholder="https://portal.acquirer.com"
                                        />
                                    </div>
                                    {errors.loginUrl && <p className="cm-err">{errors.loginUrl}</p>}
                                </label>
                                <label className="cm-label">
                                    <span>Login Email <em>*</em></span>
                                    <Input
                                        type="email"
                                        className={cn(errors.loginEmail && 'border-destructive')}
                                        name="loginEmail"
                                        value={form.loginEmail}
                                        onChange={handleChange}
                                        placeholder="admin@acquirer.com"
                                    />
                                    {errors.loginEmail && <p className="cm-err">{errors.loginEmail}</p>}
                                </label>
                                <label className="cm-label">
                                    <span>Login Password</span>
                                    <div className="relative">
                                        <Input
                                            className="pr-10"
                                            name="loginPassword"
                                            type={showPwd ? 'text' : 'password'}
                                            value={form.loginPassword}
                                            onChange={handleChange}
                                            placeholder="G��G��G��G��G��G��G��G��"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                            onClick={() => setShowPwd(!showPwd)}
                                            aria-label={showPwd ? 'Hide password' : 'Show password'}
                                        >
                                            {showPwd ? <BsEyeSlashFill /> : <BsEyeFill />}
                                        </Button>
                                    </div>
                                </label>
                                  <label className="cm-label cm-span2">
                                    <span>Notes</span>
                                    <Textarea
                                        name="notes"
                                        value={form.notes}
                                        onChange={handleChange}
                                        placeholder="Internal notes about this acquirer..."
                                        rows={4}
                                    />
                                </label>
                            </div>
                        </div>
                    )}

                    {/* G��G�� Step 3: Review G��G�� */}
                    {step === 3 && (
                        <div className="cm-step-body">
                            <h3 className="cm-step-title"><BsShieldFill /> Review &amp; Confirm</h3>
                            <div className="cm-review-grid">
                                <div className="cm-review-section">
                                    <div className="cm-review-section-title"><BsBuilding /> Basic Information</div>
                                    {[
                                        ['Acquirer Name', form.name || 'G��'],
                                        ['Country', form.country || 'G��'],
                                        ['Code', form.code || 'G��'],
                                        ['Status', form.status || 'G��'],
                                        ['Contact Email', form.contactEmail || 'G��'],
                                        ['Contact Phone', form.contactPhone || 'G��'],
                                    ].map(([k, v]) => (
                                        <div key={k} className="cm-review-row"><span>{k}</span><strong>{v}</strong></div>
                                    ))}
                                    <div className="cm-review-section-title" style={{ marginTop: '1.25rem' }}><BsGear /> Capabilities</div>
                                    <div className="cm-review-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                                        <span>Payment Types</span>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                            {(form.supportedPaymentMethods || []).length > 0 ? (
                                                (form.supportedPaymentMethods || []).map((p: { autoId: number; paymentMethod: string }) => (
                                                    <span key={`${p.autoId}-${p.paymentMethod}`} style={{ background: 'var(--color-bg)', padding: '0.2rem 0.6rem', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '0.8rem' }}>
                                                        {p.paymentMethod}
                                                    </span>
                                                ))
                                            ) : (
                                                <strong>G��</strong>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="cm-review-section">
                                    <div className="cm-review-section-title"><BsJournalText /> Access &amp; Documentation</div>
                                    {renderReviewUrl('Login URL', form.loginUrl)}
                                    <div className="cm-review-row">
                                        <span>Login Email</span>
                                        <strong>{form.loginEmail || 'G��'}</strong>
                                    </div>
                                    <div className="cm-review-row">
                                        <span>Login Password</span>
                                        <strong>{form.loginPassword ? 'G��G��G��G��G��G��G��G��' : 'G��'}</strong>
                                    </div>
                                    {renderReviewUrl('Documentation URL', form.docUrl)}
                                    <div className="cm-review-section-title" style={{ marginTop: '1.25rem' }}><BsJournalText /> Remarks</div>
                                    <div className="cm-review-row">
                                        <span>Notes</span>
                                        <strong>{form.notes?.trim() || 'No notes added'}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="cm-nav">
                        {step > 1 ? (
                            <Button type="button" variant="outline" onClick={back}>
                                <BsArrowLeft /> Back
                            </Button>
                        ) : (
                            <Button type="button" variant="outline" onClick={() => navigate('/dashboard/acquirers')}>
                                Cancel
                            </Button>
                        )}
                        <div className="flex-1" />
                        {step < 3 ? (
                            <Button type="button" onClick={next}>
                                {step === 2 ? <>Review <BsArrowRight /></> : <>Continue <BsArrowRight /></>}
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                disabled={loading}
                                onClick={submitAcquirer}
                            >
                                {loading ? (
                                    'Saving...'
                                ) : (
                                    <>
                                        <BsCheckCircleFill /> {isEdit ? 'Save Changes' : 'Finish & Create'}
                                    </>
                                )}
                            </Button>
                        )}
                    </div>

                    {apiError && (
                        <div className="cm-api-err" style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--color-error)', borderRadius: '8px', fontSize: '0.9rem', textAlign: 'center' }}>
                            {apiError}
                        </div>
                    )}

                </form>
            </div>
        </div>
    );
};

export default CreateAcquirer;
